import React, {
  useEffect,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  useFormContext,
} from 'react-hook-form';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import CurrencyTextField from 'core/components/CurrencyTextField';
import Controller from 'core/components/FormController';
import Button from 'pages/Dashboard/components/Button';
import Input from 'pages/Dashboard/components/Input';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Select from 'core/components/Select';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import ACHFields, {
  Part as ACHPart,
} from 'pages/Dashboard/pages/Billing/Payments/ACHFields';
import {
  paymentTypes,
} from 'pages/Dashboard/pages/Billing/Payments/utils';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  validateDate,
} from 'pages/Dashboard/utils/helper';
import startCase from 'lodash/startCase';

type Part = 'ach' | 'root' | 'field' | 'amount' | 'paymentDate' | 'discriminator' | 'id' | 'dynamicPanel';

type Props = {
  readonly?: boolean;
  onSubmit?: () => void;
  onCancel?: () => void;
  classes?: Partial<Record<Part, string | Partial<Record<Part, string>>>>;
};

export default function InsurancePaymentForm({
  readonly = false,
  onSubmit,
  onCancel,
  classes,
}: Props) {
  const {
    watch,
    control,
    setValue,
    trigger,
    formState: { isValid },
  } = useFormContext();
  const [paymentDateInputError, setPaymentDateInputError] = useState<string | null>(null);
  const paymentMethod = watch('insurancePayment.paymentMethod');

  useEffect(() => {
    trigger('insurancePayment.paymentDate');
  }, [paymentDateInputError, trigger]);

  const {
    discriminator,
  } = paymentMethod ?? {};

  const isCheckOrCardPayment = /^(check|card)payment/i.test(discriminator ?? '');
  const isACHPayment = /^ach/i.test(discriminator ?? '');

  useEffect(() => {
    if (!isCheckOrCardPayment) {
      setValue('insurancePayment.paymentMethod.transactionIdentifier', null);
    }
  }, [isCheckOrCardPayment, setValue]);

  return (
    <div className={clsx('flex flex-col gap-form-row w-full populate-form', classes?.root)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-form-field w-full">
        <Controller
          control={control}
          name="insurancePayment.paymentMethod.paymentAmount"
          rules={{ required: true }}
          render={({ field }) => (
            <Wrapper
              id="payment-amount"
              label="Amount"
              className={clsx('w-full', classes?.amount)}
              required
            >
              <CurrencyTextField
                {...field}
                allowNegative
                inputProps={{ readOnly: readonly }}
              />
            </Wrapper>
          )}
        />
        <Controller
          control={control}
          name="insurancePayment.paymentDate"
          rules={{
            required: true,
            validate: {
              inputError: () => isNil(paymentDateInputError) || paymentDateInputError,
              invalidValue: (value) => validateDate(value) || 'Invalid date',
            },
          }}
          render={({ field: { value, onChange, ...field }, fieldState: { error } }) => (
            <Wrapper
              id="payment-date"
              label="Payment Date"
              className={clsx('w-full', classes?.paymentDate)}
              error={!isNil(error)}
              showErrorText={false}
              required
            >
              <DatePicker
                {...field}
                value={value}
                onChange={onChange}
                containerClassName="w-full"
                inputContainerClassName="w-full"
                disabled={readonly}
                onInputErrorChange={setPaymentDateInputError}
                showDateButtons={false}
              />
            </Wrapper>
          )}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-form-field w-full">
        <div className="w-full sm:col-span-2">
          <Controller
            control={control}
            name="insurancePayment.paymentMethod.discriminator"
            rules={{ required: true }}
            render={({ field }) => (
              <Select<string>
                {...field}
                muiClasses={{
                  select: readonly ? '!pt-1.5 !text-left' : '',
                }}
                className={clsx('w-full', classes?.discriminator)}
                id="discriminator"
                label="Payment Method"
                placeholder="Select"
                options={paymentTypes}
                getValue={(option: string) => option}
                getLabel={(option: string) => startCase(option.replace('Payment', ''))}
                disabled={readonly}
                required
              />
            )}
          />
        </div>
      </div>
      <div className={clsx('w-full', classes?.dynamicPanel)}>
        {isCheckOrCardPayment && (
          <Controller
            name="insurancePayment.paymentMethod.transactionIdentifier"
            control={control}
            rules={{ required: true, validate: notEmptyString }}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                className={clsx('w-full', classes?.id)}
                label={discriminator === 'CheckPayment' ? 'Check Number' : 'Transaction ID'}
                id="transactionIdentifier"
                readOnlyValueClassName="p-2 border rounded border-gray-2 w-full"
                nonEditable={readonly}
                required
              />
            )}
          />
        )}
        {isACHPayment && !isCheckOrCardPayment && (
          <ACHFields
            classes={{
              root: '!grid grid-cols-1 sm:grid-cols-2 !gap-form-field',
              field: clsx('w-full', (classes?.ach as Partial<Record<ACHPart, string>>)?.field),
            }}
          />
        )}
      </div>
      {[onSubmit, onCancel].every((fn) => isFunction(fn)) && (
        <div className="flex flex-row gap-3">
          <Button
            onClick={onSubmit}
            color="primary"
            variant="contained"
            disabled={!isValid}
          >
            Update
          </Button>
          <Button
            variant="outlined"
            onClick={onCancel}
          >
            Close
          </Button>
        </div>
      )}
    </div>
  );
}
